import { useEffect, useState, useMemo, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { api, formatDate, formatTime } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Calendar, Trophy, Users, Newspaper, Mail, Flame, DollarSign, Cloud, TrendingUp } from "lucide-react";
import AdSlot, { ADS_ENABLED } from "@/components/ads/AdSlot";
import TrendingTagsRail from "@/components/TrendingTagsRail";

const TitansCelebration = lazy(() => import("@/components/anim/TitansCelebration"));

// Above-the-fold: keep eager so first paint has all critical UI.
import { usePulse } from "@/components/HeroPulse";
import TitanPulse from "@/components/TitanPulse";
import ArticleThumb from "@/components/ArticleThumb";
import OpponentScout from "@/components/OpponentScout";
import ArticleMeta from "@/components/ArticleMeta";
import { BuyOrSell, TitanTemperature, OneThingLearned } from "@/components/SignatureWidgets";
import { SocialProofBadge, SocialProofRow } from "@/components/SocialProof";
import { applySEO } from "@/lib/seo";
import { track } from "@/lib/analytics";

// Below-the-fold widgets — code-split so the JS for them lands only when
// the user scrolls near them. Big TBT + LCP win on mobile because these
// widgets pull in recharts, heavy data tables, and their own API calls.
const PowerRankings = lazy(() => import("@/components/PowerRankings"));
const StockWatch = lazy(() => import("@/components/StockWatch"));
const CapCorner = lazy(() => import("@/components/CapCorner"));
const TitanUpTracker = lazy(() => import("@/components/TitanUpTracker"));
const PlayersToWatch = lazy(() => import("@/components/PlayersToWatch"));
const LiveDriveChart = lazy(() => import("@/components/LiveDriveChart"));
const WinProbabilityMeter = lazy(() => import("@/components/WinProbabilityMeter"));
const DepthLeadersWidget = lazy(() => import("@/components/DepthLeadersWidget"));
const CampUpdatesWidget = lazy(() => import("@/components/CampUpdatesWidget"));
const BeatWriterFeed = lazy(() => import("@/components/BeatWriterFeed"));
const CampDigest = lazy(() => import("@/components/CampDigest"));
const SalaryCapWidget = lazy(() => import("@/components/SalaryCapWidget"));
const FeaturedQuoteCard = lazy(() => import("@/components/FeaturedQuoteCard"));
const RosterBubbleWatch = lazy(() => import("@/components/RosterBubbleWatch"));
const DepthBattles = lazy(() => import("@/components/DepthBattles"));
const FeaturedAnalysis = lazy(() => import("@/components/FeaturedAnalysis"));

// Skeleton placeholder for lazy widgets — matches typical widget height so
// the layout doesn't shift when the chunk arrives (CLS-safe).
const WidgetSkeleton = ({ h = 260 }) => (
  <div
    aria-hidden="true"
    className="rounded-2xl w-full animate-pulse"
    style={{ background: "var(--navy-850, #0B1E33)", minHeight: h }}
  />
);

const NEWSLETTER_BG = "https://customer-assets.emergentagent.com/job_titans-stadium/artifacts/bcvz8epg_image.png";

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, expired: diff === 0 };
}

export default function Home() {
  const [schedule, setSchedule] = useState([]);
  const [team, setTeam] = useState(null);
  const [news, setNews] = useState([]);
  const [email, setEmail] = useState("");
  const [loadingNews, setLoadingNews] = useState(true);
  const [featuredId, setFeaturedId] = useState(null);

  useEffect(() => {
    // News + schedule fetched INDEPENDENTLY — the hero (LCP) paints the moment
    // news arrives instead of waiting on the slower of the two requests.
    api.get("/news")
      .then((n) => {
        setNews(n.data.articles || []);
        setFeaturedId(n.data.featured_id || null);
      })
      .catch(() => toast.error("Failed to load live data"))
      .finally(() => setLoadingNews(false));
    api.get("/schedule")
      .then((s) => {
        setSchedule(s.data.events || []);
        setTeam(s.data.team || null);
      })
      .catch(() => {});
  }, []);

  const { last, next } = useMemo(() => {
    const completed = schedule.filter((e) => e.completed);
    const upcoming = schedule.filter((e) => !e.completed);
    return { last: completed[completed.length - 1] || null, next: upcoming[0] || null };
  }, [schedule]);

  const countdown = useCountdown(next?.date);
  const pulse = usePulse();

  // Real weather + odds for the next game — powers the gameday card pills.
  const [gameCtx, setGameCtx] = useState(null);
  useEffect(() => {
    api.get("/gameday/context").then((r) => setGameCtx(r.data)).catch(() => setGameCtx(null));
  }, []);

  useEffect(() => applySEO({
    title: "Tennessee Titans News, Rumors, Schedule & Analysis | The Titan Standard",
    description:
      "Daily Tennessee Titans news, rumors, training camp reports, depth chart, salary cap breakdowns and fan games — The Titan Standard is Nashville's independent Titans voice.",
    url: "https://thetitanstandard.com/",
    canonical: "https://thetitanstandard.com/",
    image: "https://thetitanstandard.com/titan-standard-logo.png",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "The Titan Standard",
        alternateName: "Tennessee Titans News — The Titan Standard",
        url: "https://thetitanstandard.com/",
      },
      {
        "@context": "https://schema.org",
        "@type": "NewsMediaOrganization",
        name: "The Titan Standard",
        url: "https://thetitanstandard.com/",
        logo: "https://thetitanstandard.com/titan-standard-logo.png",
        description: "Independent Tennessee Titans news, analysis and fan community based in Nashville.",
        knowsAbout: ["Tennessee Titans", "NFL", "AFC South", "Nissan Stadium", "NFL Draft", "NFL training camp"],
      },
    ],
  }), []);

  // Editor-pinned featured story leads; everything else stays chronological.
  // Moving (not copying) the pinned article to the front means downstream
  // slices never show it twice.
  const topStories = useMemo(() => {
    if (!featuredId) return news;
    const i = news.findIndex((a) => a.id === featuredId);
    if (i <= 0) return news;
    return [news[i], ...news.slice(0, i), ...news.slice(i + 1)];
  }, [news, featuredId]);

  // Preload the hero image the instant we know its URL so the browser can
  // fetch it in parallel with the JS/CSS that will render it. This is the
  // single biggest LCP lever for a media page.
  useEffect(() => {
    const heroImg = topStories[0]?.image;
    if (!heroImg) return;
    const src = heroImg.startsWith("/api/")
      ? `${process.env.REACT_APP_BACKEND_URL || ""}${heroImg}`
      : heroImg;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    link.fetchPriority = "high";
    document.head.appendChild(link);
    return () => {
      try { document.head.removeChild(link); } catch (e) { /* noop */ }
    };
  }, [topStories]);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      track("newsletter_signup");
      toast.success("Welcome to Two-Tone Blue Nation!");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Subscription failed";
      toast.error(msg);
    }
  };

  return (
    <div data-testid="home-page" className="pb-20">
      {/* ============================= HERO — FEATURED STORY (70) + TITAN PULSE (30) ============================= */}
      <section className="relative overflow-hidden hero-cinema" data-testid="home-hero" data-perf-lcp="hero">
        <div className="hero-glow" />
        <div className="hero-ambient" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 pt-4 md:pt-8 pb-8 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 lg:gap-8 items-start">
            {/* LEFT 70% — dominant featured story */}
            <div className="lg:col-span-7">
              <div className="font-cond uppercase tracking-[0.42em] text-[9px] text-white/35" data-testid="hero-slogan">
                Every Snap. Every Story.
              </div>
              <div className="mt-4 md:mt-8 anim-slide">
                {loadingNews ? (
                  <div className="rounded-[24px] h-[400px] sm:h-[460px] md:h-[540px] animate-pulse" style={{ background: "var(--navy-850, #0B1E33)" }} />
                ) : topStories[0] ? (
                  <Link
                    to={`/news/${topStories[0].slug || topStories[0].id}`}
                    state={{ article: topStories[0] }}
                    data-testid="hero-featured-story"
                    className="hero-featured group block relative overflow-hidden"
                  >
                    <div className="relative h-[400px] sm:h-[460px] md:h-[540px] overflow-hidden rounded-[24px]">
                      <div className="hero-featured__zoom absolute inset-0">
                        <ArticleThumb article={topStories[0]} quiet priority />
                      </div>
                      {/* Layered overlay: vertical navy gradient (darker top+bottom) + radial vignette so faces stay lit centered */}
                      <div className="absolute inset-0 hero-featured__overlay" />
                      <div className="absolute inset-0 hero-featured__vignette" />
                      <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
                        <div className="anim-rise" style={{ "--anim-delay": "0.15s" }}>
                          <div className="flex items-center gap-2 flex-wrap mb-3">
                            <span
                              data-testid="hero-featured-category"
                              className={`inline-flex items-center gap-1.5 font-cond font-bold uppercase tracking-[0.16em] text-[10px] leading-none px-2 py-1 rounded-full shadow-sm ${
                                (topStories[0].category || "").toLowerCase().includes("breaking")
                                  ? "bg-titans-red text-white"
                                  : "bg-titans-blue text-white"
                              }`}
                            >
                              {topStories[0].category || "Top Story"}
                            </span>
                            <SocialProofBadge kind="trending" />
                          </div>
                          <h1 className="hero-featured__title font-display uppercase text-3xl sm:text-5xl md:text-6xl leading-[0.95] mt-3 max-w-3xl">
                            {topStories[0].headline}
                          </h1>
                          {topStories[0].description && (
                            <p className="hero-featured__deck mt-2 md:mt-3 max-w-xl line-clamp-2" data-testid="hero-featured-deck">
                              {topStories[0].description}
                            </p>
                          )}
                          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                            <span className="font-cond uppercase tracking-[0.22em] text-[9px] text-white/45" data-testid="hero-featured-byline">
                              {topStories[0].author || "Titan Standard Staff"}
                              {topStories[0].published && <> &middot; Updated {formatDate(topStories[0].published)}</>}
                            </span>
                          </div>
                          <span className="btn-hero mt-6 inline-flex" data-testid="hero-featured-cta">
                            Read Story <ArrowRight size={14} className="btn-hero__arrow" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="rounded-[24px] h-[380px] flex items-center justify-center text-titans-silver premium-card">No stories yet.</div>
                )}
              </div>

              {/* NEXT GAMEDAY — what's happening today, right under the story */}
              <div
                data-testid="hero-split-section"
                className="mt-6 anim-rise"
                style={{ "--anim-delay": "0.18s" }}
              >
                <div className="gameday-card relative">
                  {/* Titans-blue header bar */}
                  <div className="gameday-card__header">
                    <div className="flex items-center gap-2">
                      <Calendar size={13} className="text-white/95" />
                      <span className="font-cond uppercase tracking-[0.28em] text-[11px] text-white">
                        {next?.season_type === "preseason" ? "Preseason Kickoff" : "Next Gameday"}
                      </span>
                      {next?.season_type === "preseason" && (
                        <span className="bg-titans-red text-white font-cond uppercase tracking-wider text-[9px] px-1.5 py-0.5" data-testid="countdown-pre-chip">PRE</span>
                      )}
                    </div>
                    <Link
                      to="/gameday"
                      className="gameday-card__hub group inline-flex items-center gap-1 font-cond uppercase tracking-[0.2em] text-[10px] text-white/85 hover:text-white"
                      data-testid="gameday-hub-link"
                    >
                      View Game Hub <ArrowRight size={11} className="transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </div>
                  <div className="p-4 md:p-7 grid md:grid-cols-12 gap-4 md:gap-5 items-center">
                    <div className="md:col-span-6">
                      {next ? (
                        <div className="flex items-center gap-3">
                          {next.away?.logo && (
                            <img src={next.away.logo} alt={`${next.away.abbr} team logo`} width="56" height="56" loading="lazy" decoding="async" className="h-12 w-12 md:h-14 md:w-14 object-contain shrink-0" />
                          )}
                          <div className="font-display text-2xl md:text-3xl uppercase text-white leading-none">
                            {next.away?.abbr || "TBD"}
                            <span className="block text-titans-muted text-sm md:text-base mt-1">@</span>
                            {next.home?.abbr || "TBD"}
                          </div>
                          {next.home?.logo && (
                            <img src={next.home.logo} alt={`${next.home.abbr} team logo`} width="56" height="56" loading="lazy" decoding="async" className="h-12 w-12 md:h-14 md:w-14 object-contain shrink-0" />
                          )}
                        </div>
                      ) : (
                        <div className="font-display text-3xl md:text-4xl uppercase mt-2 text-white">TBD</div>
                      )}
                      <div className="text-white/85 mt-3 font-body text-sm md:text-base">
                        {next ? `${formatDate(next.date)} · ${formatTime(next.date)}` : "Season schedule loading…"}
                      </div>
                      {next?.venue && <div className="text-white/70 text-xs mt-1 font-cond uppercase tracking-wider">{next.venue}</div>}
                      {next?.broadcast?.length > 0 && (
                        <div className="text-white/85 text-xs mt-2 font-cond uppercase tracking-wider" data-testid="countdown-broadcast">
                          <span className="text-white/60">TV:</span> {next.broadcast.join(" · ")}
                        </div>
                      )}
                      {/* Live pills: weather forecast + betting line (auto-populated) */}
                      {gameCtx && (gameCtx.weather || gameCtx.odds) && (
                        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="gameday-live-pills">
                          {gameCtx.weather && typeof gameCtx.weather.temp_f === "number" && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10 font-cond uppercase tracking-[0.16em] text-[10px] text-white/90"
                              title={gameCtx.event?.city}
                              data-testid="gameday-weather-pill"
                            >
                              <Cloud size={11} className="text-titans-blue" />
                              {gameCtx.weather.temp_f}° · {gameCtx.weather.label || "—"}
                              {typeof gameCtx.weather.wind_mph === "number" && gameCtx.weather.wind_mph > 0 && (
                                <span className="text-white/60"> · {gameCtx.weather.wind_mph}mph</span>
                              )}
                              {typeof gameCtx.weather.precip_pct === "number" && gameCtx.weather.precip_pct >= 30 && (
                                <span className="text-titans-blue"> · {gameCtx.weather.precip_pct}% rain</span>
                              )}
                            </span>
                          )}
                          {gameCtx.odds && (gameCtx.odds.details || gameCtx.odds.over_under != null) && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-titans-red/10 border border-titans-red/40 font-cond uppercase tracking-[0.16em] text-[10px] text-white"
                              title="Vegas line via ESPN"
                              data-testid="gameday-odds-pill"
                            >
                              <TrendingUp size={11} className="text-titans-red" />
                              {gameCtx.odds.details || "Even"}
                              {gameCtx.odds.over_under != null && (
                                <span className="text-white/70"> · O/U {gameCtx.odds.over_under}</span>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="mt-4"><OpponentScout /></div>
                    </div>
                    <div className="md:col-span-6 grid grid-cols-4 gap-2 md:gap-3">
                      {["Days", "Hours", "Min", "Sec"].map((label, i) => {
                        const val = countdown ? [countdown.days, countdown.hours, countdown.minutes, countdown.seconds][i] : "--";
                        return (
                          <div
                            key={label}
                            data-testid={`countdown-${label.toLowerCase()}`}
                            className="p-3 md:p-4 text-center border rounded-lg"
                            style={{ background: "#F1F5F9", borderColor: "#E2E8F0" }}
                          >
                            <div className="font-display text-3xl md:text-4xl leading-none" style={{ color: "#0C2340" }}>
                              <span key={`${label}-${val}`} className="tick-pop">{String(val).padStart(2, "0")}</span>
                            </div>
                            <div className="text-[9px] md:text-[10px] font-cond uppercase tracking-[0.25em] mt-1.5" style={{ color: "#64748B" }}>
                              {label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT 30% — Titan Pulse (sticky on desktop) */}
            <aside
              className="lg:col-span-3 lg:sticky lg:top-24 space-y-4 anim-rise"
              data-testid="titan-pulse-aside"
              style={{ "--anim-delay": "0.25s" }}
            >
              {pulse ? (
                <TitanPulse pulse={pulse} nextGame={next} trending={topStories[1]} />
              ) : (
                <WidgetSkeleton h={470} />
              )}
              {ADS_ENABLED && (
                <AdSlot
                  slot={process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR || ""}
                  format="rectangle"
                  style={{ display: "block", minHeight: 250 }}
                  testId="ad-home-sidebar"
                />
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* Trending topic tags — quick jump into what everyone's reading */}
      <TrendingTagsRail />

      <Suspense fallback={null}><TitansCelebration lastGame={last} /></Suspense>

      {/* ==================== LIVE CAMP FEED (Beat Wire) ==================== */}
      <section
        id="camp-feed"
        className="max-w-[1400px] mx-auto px-5 md:px-8 section-gap"
        data-testid="home-camp-feed"
      >
        <div className="section-head">
          <div>
            <span className="eyebrow"><Flame size={12} /> Live From Camp</span>
            <h2 className="section-title-premium text-4xl md:text-5xl mt-2">Beat Wire</h2>
          </div>
          <div className="hidden md:block text-xs font-cond uppercase tracking-[0.28em] text-ink-500" style={{ color: "var(--ink-500)" }}>
            Auto-refreshes every minute
          </div>
        </div>
        <Suspense fallback={<WidgetSkeleton h={140} />}><CampDigest /></Suspense>
        <Suspense fallback={<WidgetSkeleton h={420} />}><BeatWriterFeed /></Suspense>
      </section>

      {/* ==================== TOP STORIES ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-8 section-gap"
        data-testid="top-stories-section"
      >
        <div className="section-head">
          <div>
            <span className="eyebrow"><Newspaper size={12} /> Trending</span>
            <h2 className="section-title-premium text-4xl md:text-5xl mt-2">Top Stories</h2>
          </div>
          <Link
            to="/news"
            className="font-cond uppercase tracking-wider text-titans-blue hover:text-white text-xs md:text-sm transition-colors"
            data-testid="top-stories-view-all"
          >
            View all →
          </Link>
        </div>
        {loadingNews ? (
          <div className="text-titans-silver text-sm">Loading stories…</div>
        ) : topStories.slice(1, 6).length === 0 ? (
          <div className="text-titans-silver text-sm">No stories yet.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Lead */}
            {topStories.slice(1, 2).map((a) => (
              <Link
                key={a.id}
                to={`/news/${a.slug || a.id}`}
                state={{ article: a }}
                data-testid="top-story-lead"
                data-reveal
                className="lg:col-span-7 premium-card group block"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <ArticleThumb article={a} />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(5,16,31,0.95), rgba(5,16,31,0.30) 60%, transparent)" }} />
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                    <ArticleMeta article={a} className="mb-3" />
                    <h3 className="font-display uppercase text-2xl md:text-4xl text-white leading-[1.05] group-hover:text-titans-red transition-colors">
                      {a.headline}
                    </h3>
                    {a.description && (
                      <p className="summary-sentence text-sm md:text-base mt-3 line-clamp-2 max-w-xl">
                        {a.description}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
            {/* Rail */}
            <div className="lg:col-span-5 grid grid-cols-1 gap-4">
              {topStories.slice(2, 5).map((a, i) => (
                <Link
                  key={a.id}
                  to={`/news/${a.slug || a.id}`}
                  state={{ article: a }}
                  data-testid={`top-story-${i + 1}`}
                  data-reveal
                  style={{ "--reveal-i": i + 1 }}
                  className="premium-card group flex items-stretch"
                >
                  <div className="relative w-1/3 min-h-[110px] overflow-hidden shrink-0">
                    <ArticleThumb article={a} size="sm" />
                  </div>
                  <div className="p-4 flex flex-col justify-center flex-1 min-w-0">
                    <ArticleMeta article={a} size="xs" className="mb-1.5" />
                    <div className="font-display uppercase text-base leading-tight text-white group-hover:text-titans-red transition-colors line-clamp-3">
                      {a.headline}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ==================== SIGNATURE WIDGETS (personality voice) ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-8 mt-20 grid md:grid-cols-3 gap-5"
        data-testid="home-signature-row"
        aria-label="Signature daily widgets"
      >
        <BuyOrSell />
        <TitanTemperature />
        <OneThingLearned />
      </section>

      {/* ==================== COMMAND CENTER TEASER ==================== */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 mt-14" data-testid="home-command-center-teaser">
        <Link
          to="/command-center"
          className="group block relative overflow-hidden rounded-2xl border border-titans-blue/40 bg-gradient-to-br from-titans-navy via-titans-void to-titans-navy p-6 md:p-8 transition-colors hover:border-titans-blue"
        >
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, rgba(75,146,219,1) 0%, transparent 45%)" }} />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="font-cond uppercase tracking-[0.3em] text-[10px] text-titans-blue mb-2">The Titan Standard · New</div>
              <h3 className="font-display uppercase text-3xl md:text-4xl text-white leading-tight">
                Titans Command Center
              </h3>
              <p className="text-titans-silver font-body mt-2 max-w-xl">
                Injuries · Cap · Depth chart · Camp buzz · Rumors · Analytics · Player stock. Every signal in one dashboard.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 bg-titans-red hover:bg-titans-red-hover text-white font-cond uppercase tracking-wider text-xs px-5 py-3 transition-colors group-hover:translate-x-1 duration-300">
              Enter Command Center →
            </span>
          </div>
        </Link>
      </section>

      {/* ==================== FEATURED ANALYSIS ==================== */}
      <Suspense fallback={<WidgetSkeleton h={360} />}><FeaturedAnalysis /></Suspense>

      {/* ==================== TITAN UP TRACKER ==================== */}
      <Suspense fallback={<WidgetSkeleton h={280} />}><TitanUpTracker /></Suspense>

      {/* ==================== PLAYERS TO WATCH ==================== */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8">
        <Suspense fallback={<WidgetSkeleton h={340} />}><PlayersToWatch /></Suspense>
      </section>

      {/* ==================== ROSTER SPOTLIGHT (light editorial band) ==================== */}
      <section
        className="editorial-band editorial-band--tight mt-20"
        data-testid="home-roster-bubble"
      >
        <div className="editorial-band__ghost" aria-hidden="true">Roster&nbsp;Spotlight</div>
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-8">
          <header className="flex flex-wrap items-end justify-between gap-3 pb-5 md:pb-6">
            <div>
              <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.28em] text-[11px] text-[#C8102E]">
                <span className="inline-block h-[3px] w-6 bg-[#C8102E]" />
                <Users size={12} /> 53-Man Battles
              </span>
              <h2 className="mt-2 font-display uppercase text-[#0B1B2E] leading-[0.95] tracking-[0.01em] text-3xl md:text-5xl">
                Roster Bubble Watch
              </h2>
              <p className="mt-1 hidden md:block max-w-xl text-[13px] text-[#4B5563] leading-relaxed">
                Who's climbing, who's holding on. The players fighting for the last 53-man spots.
              </p>
            </div>
          </header>
          <div className="mb-6 md:mb-8 h-px w-full bg-gradient-to-r from-[#2E6FB4]/40 via-[#2E6FB4]/15 to-transparent" />
          <Suspense fallback={<WidgetSkeleton h={280} />}><RosterBubbleWatch /></Suspense>
        </div>
      </section>

      {/* ==================== ROSTER BATTLES (admin-editable depth chart bars) ==================== */}
      <Suspense fallback={<WidgetSkeleton h={400} />}><DepthBattles /></Suspense>

      {/* ==================== DEPTH CHART LEADERS ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-8 mt-28"
        data-testid="home-depth-chart"
      >
        <div className="section-head">
          <div>
            <span className="eyebrow"><Trophy size={12} /> Position Battles</span>
            <h2 className="section-title-premium text-4xl md:text-5xl mt-2">Depth Chart Leaders</h2>
          </div>
          <Link
            to="/depth-chart"
            className="font-cond uppercase tracking-wider text-titans-blue hover:text-white text-xs md:text-sm transition-colors"
            data-testid="depth-chart-view-all"
          >
            Full depth chart →
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Suspense fallback={<WidgetSkeleton h={320} />}><DepthLeadersWidget /></Suspense>
          <Suspense fallback={<WidgetSkeleton h={320} />}><CampUpdatesWidget /></Suspense>
        </div>
      </section>

      {/* ==================== THE BOTTOM LINE (Salary Cap + Quote — light editorial band) ==================== */}
      <section
        className="editorial-band editorial-band--tight mt-20"
        data-testid="home-salary-cap-row"
      >
        <div className="editorial-band__ghost" aria-hidden="true">The&nbsp;Bottom&nbsp;Line</div>
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-8">
          <header className="flex flex-wrap items-end justify-between gap-3 pb-5 md:pb-6">
            <div>
              <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.28em] text-[11px] text-[#C8102E]">
                <span className="inline-block h-[3px] w-6 bg-[#C8102E]" />
                <DollarSign size={12} /> Cap Intel
              </span>
              <h2 className="mt-2 font-display uppercase text-[#0B1B2E] leading-[0.95] tracking-[0.01em] text-3xl md:text-5xl">
                The Bottom Line
              </h2>
              <p className="mt-1 hidden md:block max-w-xl text-[13px] text-[#4B5563] leading-relaxed">
                Every dollar accounted for. Cap space, restructures, and the quote of the week.
              </p>
            </div>
            <Link
              to="/cap-central"
              className="group inline-flex items-center gap-1.5 font-cond uppercase tracking-[0.2em] text-xs md:text-[13px] text-[#0C2340] hover:text-[#C8102E] transition-colors"
              data-testid="bottom-line-view-all"
            >
              Cap Central
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </header>
          <div className="mb-6 md:mb-8 h-px w-full bg-gradient-to-r from-[#2E6FB4]/40 via-[#2E6FB4]/15 to-transparent" />
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <Suspense fallback={<WidgetSkeleton h={320} />}><SalaryCapWidget /></Suspense>
            </div>
            <Suspense fallback={<WidgetSkeleton h={320} />}><FeaturedQuoteCard /></Suspense>
          </div>
        </div>
      </section>

      {/* ==================== LIVE DRIVE CHART (gameday only) ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-8 mt-14 space-y-5"
        data-testid="home-live-drive-chart"
      >
        <Suspense fallback={<WidgetSkeleton h={100} />}><WinProbabilityMeter /></Suspense>
        <Suspense fallback={<WidgetSkeleton h={100} />}>
          <LiveDriveChart
            eventId={new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("drive_event") || undefined}
            forceShow={new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("drive_preview") === "1"}
          />
        </Suspense>
      </section>

      {/* ==================== RECENT RESULT ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-8 mt-20 grid md:grid-cols-3 gap-5"
        data-testid="quick-stats"
      >
        <div className="titans-card p-6 md:col-span-2" data-reveal>
          <div className="font-cond uppercase tracking-[0.25em] text-xs text-titans-muted">Last Result</div>
          {last ? (
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {last.away.logo && <img src={last.away.logo} alt={`${last.away.abbr} logo`} width="48" height="48" loading="lazy" decoding="async" className="h-12 w-12" />}
                <div className="font-display text-3xl">{last.away.abbr}</div>
                <div className="font-display text-4xl text-white">{last.away.score}</div>
              </div>
              <div className="font-cond text-titans-silver text-xs uppercase tracking-wider">Final</div>
              <div className="flex items-center gap-4">
                <div className="font-display text-4xl text-white">{last.home.score}</div>
                <div className="font-display text-3xl">{last.home.abbr}</div>
                {last.home.logo && <img src={last.home.logo} alt={`${last.home.abbr} logo`} width="48" height="48" loading="lazy" decoding="async" className="h-12 w-12" />}
              </div>
            </div>
          ) : (
            <div className="text-titans-silver mt-3">No completed games yet.</div>
          )}
        </div>
        <div className="titans-card p-6 flex flex-col justify-between" data-reveal style={{ "--reveal-i": 1 }}>
          <div>
            <div className="font-cond uppercase tracking-[0.25em] text-xs text-titans-muted">Team Record</div>
            <div className="stat-number text-white mt-2">{team?.record?.items?.[0]?.summary || "—"}</div>
          </div>
          <div className="text-titans-silver text-sm font-body">{team?.standingSummary || "AFC South"}</div>
        </div>
      </section>

      {/* ==================== LATEST COVERAGE (bento) ==================== */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 section-gap" data-testid="home-news">
        <div className="section-head">
          <div>
            <span className="eyebrow"><Newspaper size={12} /> Fresh Coverage</span>
            <h2 className="section-title-premium text-4xl md:text-5xl mt-2">Latest Coverage</h2>
          </div>
          <Link to="/news" className="font-cond uppercase tracking-wider text-titans-blue hover:text-white text-sm transition-colors">
            All news →
          </Link>
        </div>
        {loadingNews ? (
          <div className="text-titans-silver">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-12 gap-5">
              {topStories.slice(5, 10).map((a, i) => {
                const big = i === 0;
                const colSpan = big ? "md:col-span-7 md:row-span-2" : "md:col-span-5";
                return (
                  <Link
                    key={a.id}
                    to={`/news/${a.slug || a.id}`}
                    state={{ article: a }}
                    data-testid={`news-card-${i}`}
                    data-reveal
                    style={{ "--reveal-i": i }}
                    className={`relative ${colSpan} premium-card group block`}
                  >
                    <div className={`relative ${big ? "h-[440px]" : "h-[210px]"} overflow-hidden`}>
                      <ArticleThumb article={a} size={big ? "md" : "sm"} />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(5,16,31,0.95), rgba(5,16,31,0.25) 60%, transparent)" }} />
                    </div>
                    <div className={`absolute bottom-0 left-0 right-0 ${big ? "p-6" : "p-4"} z-[2]`}>
                      <ArticleMeta article={a} size="xs" className="mb-2" />
                      <h3 className={`font-display uppercase ${big ? "text-2xl md:text-3xl" : "text-base md:text-lg"} text-white leading-[1.1] group-hover:text-titans-red transition-colors duration-300`}>
                        {a.headline}
                      </h3>
                      {big && a.description && (
                        <p className="summary-sentence mt-2 line-clamp-2 max-w-xl text-sm">
                          {a.description}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
            <aside className="lg:col-span-1 space-y-5">
              <Suspense fallback={<WidgetSkeleton h={200} />}><StockWatch /></Suspense>
              <Suspense fallback={<WidgetSkeleton h={200} />}><CapCorner /></Suspense>
            </aside>
          </div>
        )}
      </section>

      {/* ==================== RANKINGS SHORTCUT ==================== */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 mt-28" data-testid="rankings-shortcut">
        <Link
          to="/news/analysis?sub=Rankings"
          data-testid="rankings-shortcut-link"
          data-reveal
          className="titans-card hover-rise group block p-6 md:p-7 overflow-hidden"
        >
          <div className="absolute right-[-40px] top-[-40px] w-56 h-56 rounded-full pointer-events-none" style={{ background: "rgba(75,146,219,0.10)", filter: "blur(60px)" }} />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <span className="eyebrow"><Trophy size={12} /> Rankings Hub</span>
              <div className="font-display text-2xl md:text-3xl uppercase leading-none mt-3 text-white">
                Editorial Rankings, All in One Place
              </div>
              <p className="summary-sentence text-sm mt-2 max-w-2xl">
                Player tiers, position-group rankings, AFC South pecking order, and weekly riser/faller lists.
              </p>
            </div>
            <span className="btn-primary shrink-0">
              Browse Rankings <ArrowRight size={14} />
            </span>
          </div>
        </Link>
      </section>

      {/* ==================== POWER RANKINGS TEASER ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-8 mt-28"
        data-testid="home-power-rankings"
      >
        <Suspense fallback={<WidgetSkeleton h={280} />}><PowerRankings compact={true} /></Suspense>
      </section>

      {/* ==================== FEATURE GRID (nav shortcuts) ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-8 mt-20 grid md:grid-cols-4 gap-4"
        data-testid="feature-grid"
      >
        {[
          { icon: Calendar, label: "Schedule", to: "/schedule", desc: "Every kickoff. Every result." },
          { icon: Users, label: "Roster", to: "/roster", desc: "53 men. One mission." },
          { icon: Trophy, label: "Stats", to: "/stats", desc: "Numbers that hit hard." },
          { icon: Newspaper, label: "News", to: "/news", desc: "Wire-fresh Titans coverage." },
        ].map(({ icon: Icon, label, to, desc }, i) => (
          <Link
            key={label}
            to={to}
            data-testid={`feature-${label.toLowerCase()}`}
            data-reveal
            style={{ "--reveal-i": i }}
            className="titans-card hover-rise group p-6 block"
          >
            <Icon size={26} className="text-titans-blue transition-transform group-hover:scale-110" />
            <div className="font-display text-2xl uppercase tracking-wide mt-4 text-white">{label}</div>
            <div className="text-sm mt-1 summary-sentence">{desc}</div>
            <div className="mt-4 font-cond uppercase tracking-wider text-xs inline-flex items-center gap-1 text-titans-blue transition-transform group-hover:translate-x-1">
              Explore <ArrowRight size={12} />
            </div>
          </Link>
        ))}
      </section>

      {/* ==================== NEWSLETTER (light editorial band) ==================== */}
      <section
        id="newsletter"
        className="editorial-band editorial-band--tight relative mt-20 overflow-hidden"
        data-testid="newsletter-section"
      >
        <div className="editorial-band__ghost" aria-hidden="true">Titan&nbsp;Wire</div>
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.28em] text-[11px] text-[#C8102E]">
              <span className="inline-block h-[3px] w-6 bg-[#C8102E]" />
              Join the huddle
            </span>
            <h2 className="mt-3 font-display uppercase text-[#0B1B2E] leading-[0.95] tracking-[0.01em] text-4xl md:text-6xl">
              Get gameday<br />in your inbox.
            </h2>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-[#4B5563]">
              Weekly recaps, injury reports, and sharp analysis. No spam. No corporate slop. Just Titans.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email-input"
              className="flex-1 border border-[#DBE2EA] bg-white focus:border-[#2E6FB4] focus:outline-none px-5 py-4 font-body text-[#0B1B2E] placeholder:text-[#94A3B8] rounded-md shadow-sm"
            />
            <button
              type="submit"
              data-testid="newsletter-submit"
              className="btn-primary"
            >
              <Mail size={16} /> Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
